import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2 } from "lucide-react";

const toLocalDateString = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};

const CalendarWidget = ({ events = [], onSelectDate, onAddEvent, onEditEvent, onDeleteEvent, onEventClick, showSelectedDateEvents = true }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const daysOfWeek = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i)
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i)
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i)
      });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    const dayStr = toLocalDateString(date);
    return events.filter(event => {
      const eventStr = String(event.date || "").slice(0, 10);
      return eventStr === dayStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    if (onSelectDate) {
      onSelectDate(date, getEventsForDate(date));
    }
  };

  const days = getDaysInMonth(currentDate);
  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="space-y-4" data-testid="calendar-widget">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">
          {months[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="prev-month-btn"
          >
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="next-month-btn"
          >
            <ChevronRight size={20} className="text-slate-600" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day headers */}
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-slate-500 py-2"
          >
            {day}
          </div>
        ))}

        {/* Days */}
        {days.map((dayInfo, index) => {
          const dayEvents = getEventsForDate(dayInfo.date);
          const hasEvents = dayEvents.length > 0;
          
          return (
            <button
              key={index}
              onClick={() => handleDateClick(dayInfo.date)}
              className={`
                relative aspect-square p-1 rounded-lg transition-all text-sm
                ${!dayInfo.isCurrentMonth ? "text-slate-300" : "text-slate-700"}
                ${isToday(dayInfo.date) ? "bg-primary text-white font-bold" : ""}
                ${isSelected(dayInfo.date) && !isToday(dayInfo.date) ? "bg-primary/20 text-primary font-medium" : ""}
                ${!isToday(dayInfo.date) && !isSelected(dayInfo.date) ? "hover:bg-slate-100" : ""}
              `}
              data-testid={`calendar-day-${dayInfo.day}`}
            >
              <span>{dayInfo.day}</span>
              {hasEvents && (
                <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex justify-center gap-0.5 pointer-events-none">
                  {dayEvents.slice(0, 3).map((_, i) => (
                    <span
                      key={i}
                      className={`rounded-full flex-shrink-0 ${
                        isToday(dayInfo.date)
                          ? "w-2 h-2 bg-white ring-2 ring-primary/40"
                          : "w-2.5 h-2.5 bg-primary ring-1 ring-primary/30"
                      }`}
                      aria-hidden
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Date Events (oculto si se muestra en la columna derecha de la página) */}
      {showSelectedDateEvents && selectedDate && (
        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-slate-700">
              {selectedDate.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
            </h4>
            {onAddEvent && (
              <button onClick={() => onAddEvent(selectedDate)} className="p-1.5 hover:bg-primary/10 text-primary rounded-lg transition-colors" title="Agregar evento">
                <Plus size={16} />
              </button>
            )}
          </div>
          {selectedEvents.length > 0 ? (
            <div className="space-y-2">
              {selectedEvents.map((event, index) => (
                <div key={event.id || index} className="p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group relative">
                  <div className={onEventClick ? "cursor-pointer pr-8" : ""} onClick={() => onEventClick && onEventClick(event)} role={onEventClick ? "button" : undefined}>
                    <p className="font-medium text-slate-800 text-sm">{event.title}</p>
                    {event.time && <p className="text-xs text-slate-500 mt-1">{event.time}</p>}
                    {event.location && <p className="text-xs text-slate-400">{event.location}</p>}
                    {event.description && (
                      <p className="text-xs text-slate-500 mt-1 truncate">
                        {/^https?:\/\//.test(String(event.description).trim()) ? (
                          <a href={event.description.trim()} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-primary hover:underline break-all">{(event.description || "").trim()}</a>
                        ) : (
                          event.description
                        )}
                      </p>
                    )}
                  </div>
                  {(onEditEvent || onDeleteEvent) && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {onEditEvent && <button type="button" onClick={(e) => { e.stopPropagation(); onEditEvent(event); }} className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-600" title="Editar"><Pencil size={14} /></button>}
                      {onDeleteEvent && <button type="button" onClick={(e) => { e.stopPropagation(); onDeleteEvent(event); }} className="p-1.5 hover:bg-rose-100 rounded-lg text-rose-600" title="Eliminar"><Trash2 size={14} /></button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No hay eventos este día</p>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarWidget;
